/**
 * Canonical admin vocabularies. These are offline-fallback labels — the backend
 * serves the authoritative enum + `x-enum-labels` for every field at runtime via
 * `/admin/collection-schemas`. Keep them in sync with the backend docschema
 * (`be-svet/internal/docschema/schemas.go`).
 *
 * A vocabulary belongs here only when the field is a CLOSED choice. A localized
 * display label (an {en,ar} leaf) is not a vocabulary, and modelling it as one
 * would stop an editor from writing the words they need.
 *
 * PHASE 01 of PLAN_client-feedback-round-2 removed the vocabularies of the eight
 * agency collections this console was forked with — they described another site's
 * industries, service icons and portfolio kinds. PHASE 02 added Svet's course
 * status/format and work category; PHASE 03 added metric kind, document category,
 * survey unit, origin kind, offering and match target.
 */

import cardMarks from '@/lib/card-marks.json';

export interface VocabOption {
  /** Stored canonical value. */
  value: string;
  /** i18n key for the human-facing label. */
  labelKey: string;
}

/**
 * Typed article-body block types (`doc.body[].type` on any long-form collection).
 * The closed union the public renderer maps to known React elements — never raw
 * HTML, so the body is injection-safe by construction. Pair it with
 * `localizedBody()` in `collections.ts`; the localizer collapses the bilingual
 * body to one locale's block array before render.
 */
export const BODY_BLOCK_TYPES: VocabOption[] = [
  { value: 'paragraph', labelKey: 'vocab.block.paragraph' },
  { value: 'subheading', labelKey: 'vocab.block.subheading' },
  { value: 'list', labelKey: 'vocab.block.list' },
  { value: 'pullquote', labelKey: 'vocab.block.pullquote' },
  { value: 'aside', labelKey: 'vocab.block.aside' },
  { value: 'figure', labelKey: 'vocab.block.figure' },
];

/**
 * A course's EDITORIAL status — where it sits in the programme. It is NOT the
 * record's publication status (draft / scheduled / published / archived), which
 * the list page filters on separately; the two share the word "status" and
 * nothing else. Values mirror `fe-svet`'s `CourseStatus` union and back the
 * public `/courses/?status=` filter, which is why the choice is closed: free text
 * would drop a course out of its own filter.
 */
export const COURSE_STATUS: VocabOption[] = [
  { value: 'open', labelKey: 'vocab.courseStatus.open' },
  { value: 'upcoming', labelKey: 'vocab.courseStatus.upcoming' },
  { value: 'current', labelKey: 'vocab.courseStatus.current' },
  { value: 'past', labelKey: 'vocab.courseStatus.past' },
];

/**
 * The course status override (round 8, D-143; backend `courseStatusOverrideChoice()`). ""
 * — "Follow the dates" — is a first-class member, like `HERO_LINE`'s, so an editor can hand
 * a course back to its dates after overriding it.
 */
export const COURSE_STATUS_OVERRIDE: VocabOption[] = [
  { value: '', labelKey: 'vocab.courseStatusOverride.none' },
  ...COURSE_STATUS,
];

/**
 * The card-mark vocabulary (D-83): the colour and icon a course card is drawn with.
 * DERIVED from `card-marks.json` — the file be-svet also derives its enums from and
 * fe-svet draws the marks with — rather than restated, so a key added there is a
 * choice here on the next build. Labels live under `vocab.cardColor.<key>` /
 * `vocab.cardIcon.<key>`; collections.test.ts fails on a key without both.
 */
export const CARD_COLOR: VocabOption[] = cardMarks.palette.map((p) => ({
  value: p.key,
  labelKey: `vocab.cardColor.${p.key}`,
}));

export const CARD_ICON: VocabOption[] = cardMarks.icons.map((i) => ({
  value: i.key,
  labelKey: `vocab.cardIcon.${i.key}`,
}));

/**
 * Which pricing tiers an offering's application offers (backend `rateScaleChoice`):
 * a tier's own `offering`, or `none` to drop the rate step.
 */
export const RATE_SCALE: VocabOption[] = [
  { value: 'course', labelKey: 'vocab.rateScale.course' },
  { value: 'community-seminar', labelKey: 'vocab.rateScale.communitySeminar' },
  { value: 'none', labelKey: 'vocab.rateScale.none' },
];

/**
 * A pricing tier's role (round 13, B-R13-14; backend `rateRoleChoice`): which of an
 * offering's `rates` prices it. Community is always from $0.
 */
export const RATE_ROLE: VocabOption[] = [
  { value: 'standard', labelKey: 'vocab.rateRole.standard' },
  { value: 'supporter', labelKey: 'vocab.rateRole.supporter' },
  { value: 'sponsor', labelKey: 'vocab.rateRole.sponsor' },
  { value: 'community', labelKey: 'vocab.rateRole.community' },
];

/** How a course is delivered. Mirrors `fe-svet`'s `CourseFormat` union. */
export const COURSE_FORMAT: VocabOption[] = [
  { value: 'online', labelKey: 'vocab.courseFormat.online' },
  { value: 'in-person', labelKey: 'vocab.courseFormat.inPerson' },
  { value: 'hybrid', labelKey: 'vocab.courseFormat.hybrid' },
];

/**
 * The four forms one conversation is fed by, and the works carousel's filter
 * (CF-44). Category also decides how a work may be rendered: only `art` is
 * routinely public domain, and `film` never ships a still (CF-45).
 */
export const WORK_CATEGORY: VocabOption[] = [
  { value: 'text', labelKey: 'vocab.workCategory.text' },
  { value: 'art', labelKey: 'vocab.workCategory.art' },
  { value: 'film', labelKey: 'vocab.workCategory.film' },
  { value: 'music', labelKey: 'vocab.workCategory.music' },
];

/**
 * Whether a figure has HAPPENED or is PLANNED (D-09). This is the one vocabulary
 * on this list that is a required field on the backend: the live site's error was
 * never the 150-student number, it was the present tense, and a metric saved with
 * no kind reaches a renderer with nothing to switch on. The backend rejects the
 * save, so the dropdown has no empty option to fall into.
 */
export const METRIC_KIND: VocabOption[] = [
  { value: 'achieved', labelKey: 'vocab.metricKind.achieved' },
  { value: 'goal', labelKey: 'vocab.metricKind.goal' },
];

/**
 * How an impact metric gets its number (round 9, D-165): typed exactly as written, or
 * computed by the CMS from the live records every time the site asks. Mirrors be-svet
 * `metricModeChoice`.
 */
export const METRIC_MODE: VocabOption[] = [
  { value: 'typed', labelKey: 'vocab.metricMode.typed' },
  { value: 'computed', labelKey: 'vocab.metricMode.computed' },
];

/**
 * What a computed impact metric counts (D-165…D-167). Mirrors be-svet
 * `metricComputeChoice`, which a backend test keeps equal to the resolver's kinds.
 */
export const METRIC_COMPUTE: VocabOption[] = [
  { value: 'students_reached', labelKey: 'vocab.metricCompute.studentsReached' },
  { value: 'countries', labelKey: 'vocab.metricCompute.countries' },
  { value: 'conflict_share', labelKey: 'vocab.metricCompute.conflictShare' },
];

/** What a downloadable document is, for grouping on `/support/#documents`. */
export const DOCUMENT_CATEGORY: VocabOption[] = [
  { value: 'legal', labelKey: 'vocab.documentCategory.legal' },
  { value: 'report', labelKey: 'vocab.documentCategory.report' },
  { value: 'budget', labelKey: 'vocab.documentCategory.budget' },
  // Round 9: material for journalists — the press kit, which used to list as a report.
  { value: 'press', labelKey: 'vocab.documentCategory.press' },
];

/**
 * Percentage POINTS versus a relative percent. "+23%" means two different things
 * depending on which of these it is, and the difference is the whole of whether
 * the survey infographic is honest (Q-52).
 */
export const SURVEY_UNIT: VocabOption[] = [
  { value: 'points', labelKey: 'vocab.surveyUnit.points' },
  { value: 'percent', labelKey: 'vocab.surveyUnit.percent' },
];

/**
 * A shaded country versus a marked programme location. The distinction is a
 * safeguarding decision expressed as data: a country row is an aggregate nobody
 * can be identified from, and only a point may carry coordinates (CF-59/CF-60).
 */
export const ORIGIN_KIND: VocabOption[] = [
  { value: 'country', labelKey: 'vocab.originKind.country' },
  { value: 'point', labelKey: 'vocab.originKind.point' },
];

/** The two things a person can apply to; their sliding scales may differ (Q-44). */
export const OFFERING: VocabOption[] = [
  { value: 'community-seminar', labelKey: 'vocab.offering.communitySeminar' },
  { value: 'course', labelKey: 'vocab.offering.course' },
];

/**
 * Where a fundraising year stands (round 9, D-161). Funded once its goal is met;
 * the derived `year_one.funded` / `year_two.funded` the home page reads come from
 * this. The match names its year by the year's `key`, a slug the client types,
 * so it is a checked text field rather than a closed choice (be-svet refuses a
 * key that names no year).
 */
export const FUNDRAISING_YEAR_STATUS: VocabOption[] = [
  { value: 'funded', labelKey: 'vocab.yearStatus.funded' },
  { value: 'in_progress', labelKey: 'vocab.yearStatus.inProgress' },
];

/**
 * What a testimonial is (round 8; was round 3's `speaker_type`, R3-49). Closed,
 * because the card SWITCHES on this: a student shows their course and country, a
 * partner their role, and a statistic its figure and caption without quotation
 * marks. A parent is a student whose role says "parent" (migration 0057).
 */
/** A project's starting point (round 8): a published work, or a line the students wrote. */
export const PROJECT_INSPIRED_BY: VocabOption[] = [
  { value: 'work', labelKey: 'vocab.projectInspiredBy.work' },
  { value: 'own_line', labelKey: 'vocab.projectInspiredBy.ownLine' },
];

export const TESTIMONIAL_KIND: VocabOption[] = [
  { value: 'student', labelKey: 'vocab.testimonialKind.student' },
  // Round 9 (R9-20): a parent's quote, shown with their role and country.
  { value: 'parent', labelKey: 'vocab.testimonialKind.parent' },
  { value: 'partner', labelKey: 'vocab.testimonialKind.partner' },
  { value: 'statistic', labelKey: 'vocab.testimonialKind.statistic' },
];

/**
 * What a piece of press coverage IS (R3-64, R3-66). You read an article and you listen to
 * a podcast. `/press/` grouped its page by it server-side, which is why it is both closed
 * here and a backend facet indexed by be-svet migration 0026; that page is Contact's "For
 * journalists" card since round 9, and no public page renders the value now.
 */
export const MEDIA_TYPE: VocabOption[] = [
  { value: 'article', labelKey: 'vocab.mediaType.article' },
  { value: 'podcast', labelKey: 'vocab.mediaType.podcast' },
  { value: 'video', labelKey: 'vocab.mediaType.video' },
  { value: 'broadcast', labelKey: 'vocab.mediaType.broadcast' },
];

/**
 * How much of a presence a country has on the origins map (R3-53). The TIER is what
 * the site publishes; the exact count stays in the console as the editor's working
 * figure. A shaded country labelled "1 student" is a number a reader can put a name
 * to — a band cannot, which is the whole point of banding it.
 */
export const ORIGIN_TIER: VocabOption[] = [
  { value: 'high', labelKey: 'vocab.originTier.high' },
  { value: 'some', labelKey: 'vocab.originTier.some' },
  { value: 'light', labelKey: 'vocab.originTier.light' },
];

/**
 * Which of the hero's two credibility lines a partner is named on (CF-03). `''` is a
 * MEMBER of the vocabulary, not a missing value: almost every partner is on neither line,
 * and an editor who picks one by mistake has to be able to put the record back — which a
 * two-option dropdown cannot do. It is a plain `enumField` rather than an
 * `unsettableEnumField` because what the site switches on is the empty STRING, not null,
 * and `Select` already maps '' to and from its own sentinel for Radix.
 *
 * The order is the order the options are offered in, and "Not in the hero" is first
 * because it is what the great majority of records are.
 */
export const HERO_LINE: VocabOption[] = [
  { value: '', labelKey: 'vocab.heroLine.none' },
  { value: 'partnership', labelKey: 'vocab.heroLine.partnership' },
  { value: 'founded_by', labelKey: 'vocab.heroLine.foundedBy' },
];

/**
 * How large a partner's mark sits in its equal logo box on the home page (round 8,
 * note 08): 85%, 100% or 120% of the box, never past it. Three steps only, so the
 * row stays even. Mirrors be-svet's `logoSizeChoice`; an absent value is `normal`.
 */
export const LOGO_SIZE: VocabOption[] = [
  { value: 'smaller', labelKey: 'vocab.logoSize.smaller' },
  { value: 'normal', labelKey: 'vocab.logoSize.normal' },
  { value: 'larger', labelKey: 'vocab.logoSize.larger' },
];

/**
 * Which group a partner sits in on `/partners/` (round 11, note 38): "Who we work with."
 * or "Who has funded the work". Mirrors be-svet's `partnerKindChoice`; an absent value is
 * `partner`.
 */
export const PARTNER_KIND: VocabOption[] = [
  { value: 'partner', labelKey: 'vocab.partnerKind.partner' },
  { value: 'funder', labelKey: 'vocab.partnerKind.funder' },
];

/** Build the `x-enum-labels` map a JSON-schema enum field uses to show friendly text. */
export function enumLabels(options: VocabOption[]): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.value, o.labelKey]));
}

/** Build `{ value, label }` option list for a plain Select, resolving labels through t(). */
export function vocabSelectOptions(
  options: VocabOption[],
  t: (key: string) => string,
): { value: string; label: string }[] {
  return options.map((o) => ({ value: o.value, label: t(o.labelKey) }));
}
